import { AnimatePresence, motion, type Variants } from "motion/react";
import { useLocation, useOutlet } from "react-router-dom";
import { useRef, useLayoutEffect } from "react";
import { cn } from "@/lib/utils";
import { isSameCampaignDifferentRoute } from "@/utils/route-helpers";
import {
  springConfig,
  exitSpringConfig,
  frameloopFade,
} from "@/utils/animations";

interface AnimatedOutletProps {
  className?: string;
}

// Single source of truth: routes in horizontal order (left to right)
const ROUTE_ORDER = [
  { type: "profile", match: (path: string) => path === "/profile" },
  { type: "home", match: (path: string) => path === "/" },
  { type: "campaign", match: (path: string) => path.startsWith("/campaign") },
] as const;

type RouteType = (typeof ROUTE_ORDER)[number]["type"];

const getRouteInfo = (
  pathname: string
): { type: RouteType; position: number } => {
  for (let i = 0; i < ROUTE_ORDER.length; i++) {
    if (ROUTE_ORDER[i].match(pathname)) {
      return { type: ROUTE_ORDER[i].type, position: i };
    }
  }
  // Fallback: find home's position or default to center
  const homeIndex = ROUTE_ORDER.findIndex((r) => r.type === "home");
  return {
    type: "home",
    position: homeIndex >= 0 ? homeIndex : Math.floor(ROUTE_ORDER.length / 2),
  };
};

let transitionDirectionsRef: {
  enterFrom: "left" | "right";
  exitTo: "left" | "right";
} | null = null;

// Create variants that read from shared ref
const createSlideVariants = () => {
  return {
    initial: () => {
      const dir = transitionDirectionsRef?.enterFrom ?? "right";
      return {
        opacity: 0,
        x: dir === "right" ? 16 : -16,
      };
    },
    animate: {
      opacity: 1,
      x: 0,
      transition: {
        x: springConfig,
        opacity: {
          duration: 0.15,
          ease: [0.4, 0, 0.2, 1],
          ...frameloopFade,
        },
      },
    },
    exit: () => {
      const dir = transitionDirectionsRef?.exitTo ?? "left";
      return {
        opacity: 0,
        x: dir === "right" ? 16 : -16,
        transition: {
          x: exitSpringConfig,
          opacity: {
            duration: 0.12,
            ease: [0.4, 0, 0.6, 1],
            ...frameloopFade,
          },
        },
      };
    },
  } as Variants;
};

// Calculate transition directions based on route positions
const calculateTransition = (
  fromPosition: number,
  toPosition: number
): {
  newRouteEntersFrom: "left" | "right";
  oldRouteExitsTo: "left" | "right";
} => {
  const isMovingRight = toPosition > fromPosition;

  if (isMovingRight) {
    return { newRouteEntersFrom: "right", oldRouteExitsTo: "left" };
  } else {
    return { newRouteEntersFrom: "left", oldRouteExitsTo: "right" };
  }
};

export const AnimatedOutlet = ({ className }: AnimatedOutletProps) => {
  const location = useLocation();
  const outlet = useOutlet();
  const previousPathnameRef = useRef(location.pathname);

  // Track previous route info
  const previousRouteInfoRef = useRef(getRouteInfo(location.pathname));
  const currentRouteInfo = getRouteInfo(location.pathname);
  const previousRouteInfo = previousRouteInfoRef.current;

  // Check if we're switching between campaign sub-routes (same campaign, different sub-route)
  const previousPathname = previousPathnameRef.current;
  const isSwitchingBetweenCampaignSubRoutes = isSameCampaignDifferentRoute(
    previousPathname,
    location.pathname
  );

  // Calculate transition directions based on positions
  const transition = calculateTransition(
    previousRouteInfo.position,
    currentRouteInfo.position
  );

  // Store transition directions in shared ref (accessible by both components)
  transitionDirectionsRef = {
    enterFrom: transition.newRouteEntersFrom,
    exitTo: transition.oldRouteExitsTo,
  };

  // Create variants (reads from shared ref)
  const variants = createSlideVariants();

  // Update previous route info after render
  useLayoutEffect(() => {
    previousRouteInfoRef.current = currentRouteInfo;
    previousPathnameRef.current = location.pathname;
  }, [currentRouteInfo, location.pathname]);

  // If switching between campaign sub-routes, render without animation
  if (isSwitchingBetweenCampaignSubRoutes) {
    return <div className={cn("h-full w-full", className)}>{outlet}</div>;
  }

  return (
    <AnimatePresence mode="wait" initial={false}>
      {outlet && (
        <motion.div
          key={location.pathname}
          variants={variants}
          initial="initial"
          animate="animate"
          exit="exit"
          className={cn("h-full w-full", className)}
        >
          {outlet}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
